import axios from 'axios';
import type { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { getAuth } from 'firebase/auth';
import { handleAxiosApiError } from './apiErrorHandler';
import { API_CONFIG } from '../config/api';
import { logError, logWarn, logInfo } from './logtail';


// Create axios instance with default config
const createApiClient = (): AxiosInstance => {
  const client = axios.create({
    baseURL: API_CONFIG.BASE_URL,
    timeout: API_CONFIG.TIMEOUT,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Request interceptor to add Firebase auth token
  client.interceptors.request.use(
    async (config) => {
      try {
        const auth = getAuth();
        const user = auth.currentUser;
        if (user) {
          const token = await user.getIdToken(); // Get cached token (auto-refreshes only when needed)
          config.headers.Authorization = `Bearer ${token}`;
        }
      } catch (error) {
        logWarn('Failed to get auth token', { error });
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor for error handling
  client.interceptors.response.use(
    (response: AxiosResponse) => response,
    (error) => {
      if (error.response) {
        // Server responded with error status
        const { status, data } = error.response;
        
        switch (status) {
          case 401:
            logError('Unauthorized - please log in again', { status });
            break;
          case 403:
            logError('Forbidden - insufficient permissions', { status });
            break;
          case 404:
            logWarn('Resource not found', { status });
            break;
          case 413:
            logWarn('File too large', { status });
            break;
          case 500:
            logError('Server error', { status });
            break;
          default:
            logError(`HTTP ${status}: Request failed`, { status });
        }

        handleAxiosApiError(error);
      } else if (error.request) {
        logError('Network error - please check your connection');
      } else {
        logError('Request error', { message: error.message });
      }
      
      return Promise.reject(error);
    }
  );

  // Optional: log slow requests (warn if > 2s, info if > 1s) without spamming successes
  client.interceptors.request.use((config) => {
    (config as any).metadata = { start: Date.now() };
    return config;
  });
  client.interceptors.response.use((response) => {
    const meta = (response.config as any).metadata;
    if (meta && meta.start) {
      const durationMs = Date.now() - meta.start;
      if (durationMs > 2000) {
        logWarn('Slow API response', { url: response.config.url, method: response.config.method, durationMs });
      } else if (durationMs > 1000 && import.meta.env.DEV) {
        logInfo('API response >1s (dev only)', { url: response.config.url, method: response.config.method, durationMs });
      }
    }
    return response;
  });

  return client;
};

// API client instance
export const apiClient = createApiClient();

// Generic API request helper
export const apiRequest = async <T>(
  config: AxiosRequestConfig
): Promise<T> => {
  try {
    const response = await apiClient(config);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// File upload helper
export const uploadFile = async (
  url: string,
  file: File,
  onProgress?: (progress: number) => void
): Promise<any> => {
  const formData = new FormData();
  formData.append('file', file);

  try {
    const response = await apiClient.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (progressEvent.total && onProgress) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(progress);
        }
      },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Type definitions for API responses
export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  errorMessage?: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
}

// Stripe API functions
export const createCheckoutSession = async (priceId: string): Promise<ApiResponse<{ sessionUrl: string }>> => {
  return apiRequest<ApiResponse<{ sessionUrl: string }>>({
    method: 'POST',
    url: '/stripe/create-checkout-session',
    data: { priceId },
  });
};

export const createUpgradeSession = async (priceId: string): Promise<ApiResponse<{ sessionUrl: string }>> => {
  return apiRequest<ApiResponse<{ sessionUrl: string }>>({
    method: 'POST',
    url: '/stripe/create-upgrade-session',
    data: { priceId },
  });
};

export const createCustomerPortalSession = async (returnUrl: string): Promise<ApiResponse<{ portalUrl: string }>> => {
  return apiRequest<ApiResponse<{ portalUrl: string }>>({
    method: 'POST',
    url: '/stripe/create-portal-session',
    data: { returnUrl },
  });
};
